import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiCall } from '@/utils/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  User,
  Phone,
  Mail,
  Tag,
  Clock,
  ArrowRightLeft,
  MessageSquare,
  CheckSquare,
  Zap,
  Trash2,
  Link as LinkIcon,
} from 'lucide-react';

interface TimelineEntry {
  type: 'event' | 'note' | 'task';
  subtype: string;
  timestamp: string;
  created_by: number | null;
  detail: string | null;
  data: any;
}

interface LeadData {
  kommo_id: number;
  name: string;
  price: string;
  status_id: number;
  status_name?: string;
  pipeline_id: number;
  responsible_user_id: number;
  responsible_user_name: string;
  is_deleted: boolean;
  closed_at: string | null;
  kommo_created_at: string;
  kommo_updated_at: string;
  tags: string[];
}

interface ContactData {
  kommo_id: number;
  name: string;
  phone: string | null;
  email: string | null;
}

interface LeadTimelineResponse {
  lead: LeadData;
  contacts: ContactData[];
  timeline: TimelineEntry[];
  seller_map: Record<string, string>;
  status_map?: Record<string, string>;
}

const EVENT_ICONS: Record<string, { icon: React.ElementType; color: string }> = {
  lead_status_changed: { icon: ArrowRightLeft, color: 'bg-blue-100 text-blue-600' },
  entity_responsible_changed: { icon: User, color: 'bg-violet-100 text-violet-600' },
  entity_tag_added: { icon: Tag, color: 'bg-amber-100 text-amber-600' },
  common_note_added: { icon: MessageSquare, color: 'bg-emerald-100 text-emerald-600' },
  entity_linked: { icon: LinkIcon, color: 'bg-cyan-100 text-cyan-600' },
  lead_added: { icon: Zap, color: 'bg-green-100 text-green-600' },
  outgoing_mail: { icon: Mail, color: 'bg-indigo-100 text-indigo-600' },
  incoming_mail: { icon: Mail, color: 'bg-sky-100 text-sky-600' },
  lead_deleted: { icon: Trash2, color: 'bg-red-100 text-red-600' },
  note: { icon: MessageSquare, color: 'bg-emerald-100 text-emerald-600' },
  task: { icon: CheckSquare, color: 'bg-orange-100 text-orange-600' },
};

const EVENT_LABELS: Record<string, string> = {
  lead_status_changed: 'Changement de statut',
  entity_responsible_changed: 'Changement de responsable',
  entity_tag_added: 'Tag ajoute',
  entity_tag_deleted: 'Tag retire',
  common_note_added: 'Note ajoutee',
  common_note_deleted: 'Note supprimee',
  entity_linked: 'Liaison',
  entity_unlinked: 'Deliaison',
  name_field_changed: 'Nom modifie',
  lead_added: 'Lead cree',
  lead_deleted: 'Lead supprime',
  lead_restored: 'Lead restaure',
  outgoing_mail: 'Email envoye',
  incoming_mail: 'Email recu',
  entity_merged: 'Fusion',
  entity_direct_message: 'Message direct',
  service_note_added: 'Note service',
  common: 'Note',
  amomail_message: 'Email',
  task: 'Tache',
};

interface LeadDetailDialogProps {
  kommoId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LeadDetailDialog: React.FC<LeadDetailDialogProps> = ({ kommoId, open, onOpenChange }) => {
  const { token } = useAuth();
  const [data, setData] = useState<LeadTimelineResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchTimeline = useCallback(async (id: number) => {
    setLoading(true);
    try {
      const res = await apiCall<{ success: boolean; data: LeadTimelineResponse; message?: string }>(
        token,
        `/kommo/analytics/lead/${id}/timeline`
      );
      if (res.success) {
        setData(res.data);
      } else {
        toast.error(res.message || 'Lead non trouve');
        setData(null);
      }
    } catch (err: any) {
      const msg = err?.message?.includes('404') ? 'Lead non trouve' : 'Erreur lors du chargement du lead';
      toast.error(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open && kommoId) {
      fetchTimeline(kommoId);
    }
    if (!open) {
      setData(null);
    }
  }, [open, kommoId, fetchTimeline]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const getEventIcon = (type: string, subtype: string) => {
    if (type === 'note') return EVENT_ICONS.note || EVENT_ICONS.common_note_added;
    if (type === 'task') return EVENT_ICONS.task;
    return EVENT_ICONS[subtype] || { icon: Zap, color: 'bg-slate-100 text-slate-600' };
  };

  const getEventLabel = (type: string, subtype: string) => {
    if (type === 'event') {
      if (EVENT_LABELS[subtype]) return EVENT_LABELS[subtype];
      if (subtype.startsWith('custom_field_') && subtype.endsWith('_value_changed')) return 'Champ modifie';
      return subtype;
    }
    if (type === 'note') return EVENT_LABELS[subtype] || 'Note';
    return 'Tache';
  };

  const getSellerName = (id: number | null) => {
    if (id === null || id === undefined || !data) return 'Systeme';
    return data.seller_map[String(id)] || `#${id}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[60vw] max-w-[60vw] sm:max-w-[60vw] max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-xl font-bold text-slate-900">
            {loading ? 'Chargement...' : data ? (data.lead.name || 'Lead sans nom') : 'Lead'}
          </DialogTitle>
          {data && (
            <DialogDescription className="text-sm text-slate-500">
              #{data.lead.kommo_id} - Cree le {formatDate(data.lead.kommo_created_at)}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : data ? (
          <div className="flex-1 flex min-h-0 px-6 pb-6 pt-4 gap-6">
            {/* Left column: Lead info + Contacts */}
            <div className="w-[320px] shrink-0 space-y-5 overflow-y-auto">
              {/* Lead details */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Informations</p>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Statut</span>
                  <Badge variant={data.lead.is_deleted ? 'destructive' : 'secondary'} className="text-xs">
                    {data.lead.is_deleted ? 'Supprime' : data.lead.status_name || data.status_map?.[String(data.lead.status_id)] || `#${data.lead.status_id}`}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Responsable</span>
                  <span className="font-medium text-slate-900 text-right">{data.lead.responsible_user_name}</span>
                </div>
                {data.lead.price && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Prix</span>
                    <span className="font-medium">{parseFloat(data.lead.price).toLocaleString('fr-FR')} EUR</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Mis a jour</span>
                  <span className="text-sm text-slate-600">{formatDate(data.lead.kommo_updated_at)}</span>
                </div>
              </div>

              {/* Tags */}
              {data.lead.tags.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.lead.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Contacts */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Contacts ({data.contacts.length})
                </p>
                {data.contacts.length > 0 ? (
                  <div className="space-y-2">
                    {data.contacts.map((c) => (
                      <div key={c.kommo_id} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <p className="font-medium text-sm text-slate-900">{c.name || 'Sans nom'}</p>
                        {c.phone && (
                          <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
                            <Phone className="h-3 w-3" /> {c.phone}
                          </p>
                        )}
                        {c.email && (
                          <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                            <Mail className="h-3 w-3" /> {c.email}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Aucun contact lie</p>
                )}
              </div>
            </div>

            {/* Right column: Timeline (scrollable) */}
            <div className="flex-1 min-w-0 border-l border-slate-100 pl-6 flex flex-col">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3 shrink-0">
                Chronologie ({data.timeline.length} evenements)
              </p>
              <div className="flex-1 overflow-y-auto pr-1">
                <div className="relative">
                  <div className="absolute left-[18px] top-0 bottom-0 w-px bg-slate-200" />
                  <div className="space-y-4">
                    {data.timeline.map((entry, index) => {
                      const { icon: Icon, color } = getEventIcon(entry.type, entry.subtype);
                      const label = getEventLabel(entry.type, entry.subtype);
                      return (
                        <div key={`${entry.type}-${entry.subtype}-${entry.timestamp}-${index}`} className="relative flex items-start gap-3 pl-1">
                          <div className={cn('relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 border-white shrink-0', color)}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 pb-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-slate-900">{label}</span>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{entry.type}</Badge>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                              <Clock className="h-3 w-3 shrink-0" />
                              {formatDate(entry.timestamp)}
                              <span className="mx-0.5">-</span>
                              {getSellerName(entry.created_by)}
                            </p>
                            {entry.type === 'task' ? (
                              <div className="mt-1.5 text-sm text-slate-600 bg-slate-50 p-2.5 rounded space-y-1.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {entry.data?.task_type && (
                                    <Badge variant="outline" className="text-[11px] px-2 py-0.5">{entry.data.task_type}</Badge>
                                  )}
                                  {entry.data?.is_completed ? (
                                    <Badge variant="default" className="text-[11px] px-2 py-0.5">Termine</Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-[11px] px-2 py-0.5">En cours</Badge>
                                  )}
                                  {entry.data?.result && (
                                    <Badge variant="outline" className="text-[11px] px-2 py-0.5 border-amber-300 text-amber-700">{entry.data.result}</Badge>
                                  )}
                                </div>
                                {entry.data?.text && <p>{entry.data.text}</p>}
                                {entry.data?.deadline && (
                                  <p className="text-xs text-slate-400">Echeance: {entry.data.deadline}</p>
                                )}
                              </div>
                            ) : entry.detail ? (
                              <div className="mt-1.5 text-sm text-slate-600 bg-slate-50 p-2 rounded">
                                {entry.detail}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            Lead non trouve
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadDetailDialog;
